import { SvgIcon } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { useAppDispatch, useAppSelector } from '@/shared/model';
import { Button, DataRecordCard } from '@/shared/ui';
import { deleteIntegration, selectIsLoading } from '../../model/slice';
import type {
  Datasource,
  MicrosoftAdsReportingIntegration,
} from '../../model/types';
import { useDatasourceIntegrations } from '../../model/useDatasourceIntegrations';
import { useURLSearchParamsBroadcaster } from '../../model/useURLSearchParamsBroadcaster';
import { DatasourcePanel } from '../DatasourcePanel/DatasourcePanel';
import { IntegrationSyncInfo } from '../IntegrationSyncInfo/IntegrationSyncInfo';
import { BROADCAST_CHANNEL_NAME } from './model/constants';
import { useMicrosoftAdsCode } from './model/useMicrosoftAdsCode';

const MicrosoftAdsButton = styled(Button)(() => ({
  border: '1px solid #8c8c8c',
  backgroundColor: 'white',
  borderRadius: 0,
  color: '#5e5e5e',
  padding: 12,
  height: 41,

  '&:hover': {
    backgroundColor: 'white',
    textDecoration: 'underline',
  },

  '& .MuiButton-startIcon': {
    marginRight: 12,
    marginLeft: 0,
  },
}));

interface Props {
  datasource: Datasource;
}

export function MicrosoftAdsDatasource({ datasource }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const integrationsLoading = useAppSelector(selectIsLoading);
  const microsoftAdsReportingIntegrations = useDatasourceIntegrations(
    datasource,
  ) as MicrosoftAdsReportingIntegration[];
  const { processing, openDataAccessDialog } = useMicrosoftAdsCode();

  useURLSearchParamsBroadcaster({
    channelName: BROADCAST_CHANNEL_NAME,
    params: ['code'],
  });

  const handleDeleteIntegration = (id: string) => () => {
    return dispatch(deleteIntegration(id))
      .unwrap()
      .then(() => {
        enqueueSnackbar('Integration successfully deleted.', {
          variant: 'success',
          autoHideDuration: 2000,
        });
      })
      .catch(() => {
        enqueueSnackbar('Unable to delete integration. Please, try again.', {
          variant: 'error',
          autoHideDuration: 4000,
        });
      });
  };

  return (
    <DatasourcePanel
      loading={integrationsLoading || processing}
      datasource={datasource}
      addButtonSlot={
        <MicrosoftAdsButton
          startIcon={
            <SvgIcon>
              <svg
                aria-hidden="true"
                viewBox="0 0 25 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.5216 0.5H0V11.9067H11.5216V0.5Z"
                  fill="#f25022"
                ></path>
                <path
                  d="M24.2418 0.5H12.7202V11.9067H24.2418V0.5Z"
                  fill="#7fba00"
                ></path>
                <path
                  d="M11.5216 13.0933H0V24.5H11.5216V13.0933Z"
                  fill="#00a4ef"
                ></path>
                <path
                  d="M24.2418 13.0933H12.7202V24.5H24.2418V13.0933Z"
                  fill="#ffb900"
                ></path>
              </svg>
            </SvgIcon>
          }
          onClick={openDataAccessDialog}
        >
          Add account
        </MicrosoftAdsButton>
      }
      integrationsSlot={microsoftAdsReportingIntegrations.map((integration) => {
        return (
          <DataRecordCard
            key={integration.id}
            recordNoun="integration"
            title={integration.integrationConfig.userEmail}
            titleExtra={
              <IntegrationSyncInfo
                status={integration.status}
                lastSuccessfulSync={integration.lastSuccessfulSync}
              />
            }
            onDelete={handleDeleteIntegration(integration.id)}
          />
        );
      })}
    />
  );
}
